import { useMemo, useState } from 'react';

/**
 * Picking a product icon.
 *
 * This used to be a text field labelled "Icon", which meant staff had to know
 * their OS emoji shortcut to fill it in — so in practice every product kept the
 * default. A browsable grid turns the icon from a field you skip into a choice
 * you make in one tap, which is the difference between a shelf of identical
 * cards and one you can scan.
 *
 * Emoji rather than uploaded images, deliberately: the deployment has no object
 * store, no image pipeline and no CDN, and a glyph the OS already ships renders
 * crisply at every size on every device with no bytes and no moderation problem.
 */
interface IconGroup {
  label: string;
  icons: readonly (readonly [icon: string, keywords: string])[];
}

const ICON_GROUPS: readonly IconGroup[] = [
  {
    label: 'Drinks',
    icons: [
      ['🥤', 'cup soda coke cola soft drink'],
      ['🧃', 'juice box popper solo'],
      ['🧊', 'ice cold chilled pepsi'],
      ['🫧', 'bubbles fizz sparkling lemonade'],
      ['💧', 'water still drop mineral'],
      ['⚡', 'energy bull boost'],
      ['🍋', 'lemon lime sprite squash'],
      ['🍊', 'orange fanta citrus'],
      ['🍒', 'cherry dr pepper berry'],
      ['🍏', 'green apple dew citrus'],
      ['🥛', 'milk dairy iced coffee'],
      ['☕', 'coffee hot espresso'],
      ['🍵', 'tea green iced'],
      ['🧋', 'bubble tea boba'],
      ['🍺', 'beer ale lager'],
      ['🍻', 'beers cheers pack'],
      ['🍷', 'wine red'],
      ['🥂', 'champagne sparkling toast'],
      ['🍾', 'bottle pop sparkling'],
      ['🍹', 'cocktail tropical punch'],
      ['🍸', 'cocktail martini mixer'],
      ['🥥', 'coconut water tropical'],
      ['🫗', 'pouring pour jug'],
      ['🍯', 'honey syrup sweet'],
    ],
  },
  {
    label: 'Fruit',
    icons: [
      ['🍎', 'apple red'],
      ['🍐', 'pear'],
      ['🍓', 'strawberry berry'],
      ['🫐', 'blueberry berries'],
      ['🍇', 'grapes vine'],
      ['🍉', 'watermelon melon'],
      ['🍈', 'melon rockmelon'],
      ['🍑', 'peach stone fruit'],
      ['🍍', 'pineapple tropical'],
      ['🥭', 'mango tropical'],
      ['🍌', 'banana'],
      ['🥝', 'kiwi fruit'],
      ['🥑', 'avocado'],
      ['🥥', 'coconut'],
    ],
  },
  {
    label: 'Fresh',
    icons: [
      ['🍅', 'tomato'],
      ['🥒', 'cucumber'],
      ['🥕', 'carrot'],
      ['🥔', 'potato'],
      ['🧅', 'onion brown'],
      ['🧄', 'garlic'],
      ['🫑', 'capsicum pepper'],
      ['🌶️', 'chilli hot pepper'],
      ['🥦', 'broccoli cauliflower'],
      ['🥬', 'lettuce spinach greens leafy'],
      ['🌽', 'corn cob'],
      ['🍆', 'eggplant aubergine'],
      ['🎃', 'pumpkin'],
      ['🍠', 'sweet potato kumara'],
      ['🍄', 'mushroom'],
      ['🌿', 'herbs organic fresh'],
    ],
  },
  {
    label: 'Food',
    icons: [
      ['🍞', 'bread loaf'],
      ['🥐', 'croissant pastry'],
      ['🥖', 'baguette stick'],
      ['🥨', 'pretzel'],
      ['🧀', 'cheese dairy'],
      ['🥚', 'egg eggs'],
      ['🥓', 'bacon meat'],
      ['🍗', 'chicken meat'],
      ['🍕', 'pizza'],
      ['🥪', 'sandwich roll'],
      ['🌮', 'taco'],
      ['🍟', 'chips fries hot'],
      ['🍿', 'popcorn snack'],
      ['🍪', 'biscuit cookie'],
      ['🍫', 'chocolate block'],
      ['🍬', 'lolly candy sweets'],
      ['🍩', 'donut'],
      ['🧁', 'cupcake'],
      ['🎂', 'cake birthday'],
      ['🍦', 'ice cream soft serve'],
      ['🥫', 'tin can tinned'],
      ['🫙', 'jar preserve'],
      ['🧂', 'salt pantry'],
      ['🍚', 'rice grain'],
    ],
  },
  {
    label: 'Household',
    icons: [
      ['🧻', 'toilet paper towel'],
      ['🧼', 'soap wash'],
      ['🧴', 'bottle lotion detergent'],
      ['🧽', 'sponge scourer'],
      ['🧹', 'broom cleaning'],
      ['🪣', 'bucket'],
      ['🔋', 'battery'],
      ['💡', 'bulb light'],
      ['🪥', 'toothbrush dental'],
      ['🧺', 'basket laundry'],
      ['📦', 'box carton bulk'],
      ['🛒', 'trolley cart'],
      ['🎈', 'balloon party'],
      ['🕯️', 'candle'],
      ['♻️', 'recycle refund container'],
      ['🧯', 'extinguisher safety'],
    ],
  },
  {
    label: 'Signs',
    icons: [
      ['⭐', 'star favourite best'],
      ['🔥', 'hot popular fast'],
      ['❄️', 'frozen cold freezer'],
      ['🏷️', 'tag price special'],
      ['💎', 'premium quality'],
      ['🆕', 'new arrival'],
      ['🥇', 'first best gold'],
      ['🎯', 'target deal'],
      ['🍀', 'lucky'],
      ['🌞', 'summer sunny'],
      ['🌙', 'night late'],
      ['🎁', 'gift bundle'],
    ],
  },
];

const ALL_ICONS = ICON_GROUPS.flatMap((group) => group.icons);

export function IconPicker({
  value,
  onChange,
  label = 'Icon',
}: {
  value: string;
  onChange: (icon: string) => void;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [group, setGroup] = useState(ICON_GROUPS[0]?.label ?? 'Drinks');

  const shown = useMemo(() => {
    const term = query.trim().toLowerCase();
    // Searching spans every group: someone typing "cola" should not have to
    // guess which tab it lives under first.
    if (term) return ALL_ICONS.filter(([icon, words]) => words.includes(term) || icon === term);
    return ICON_GROUPS.find((entry) => entry.label === group)?.icons ?? [];
  }, [query, group]);

  return (
    <div>
      <span className="sign block text-[11px] text-ink-700">{label}</span>

      <div className="mt-1.5 flex items-center gap-2">
        <span className="medallion" aria-hidden>
          {value || '🥤'}
        </span>
        <button
          type="button"
          onClick={() => setOpen((wasOpen) => !wasOpen)}
          aria-expanded={open}
          className="sign rounded-[3px] border border-paper-300 bg-paper-50 px-3 py-2 text-[11px] text-ink-700 hover:border-paper-400 hover:bg-paper-100"
        >
          {open ? 'Done' : 'Choose icon'}
        </button>
      </div>

      {open && (
        <div className="well mt-2 p-3">
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search — cola, lemon, frozen…"
            className="w-full rounded-[3px] border border-paper-300 bg-paper-50 px-3 py-1.5 text-[14px] text-ink-900 placeholder:text-ink-500/60 focus:border-leaf-600"
          />

          {!query.trim() && (
            <div className="mt-2.5 flex flex-wrap gap-1">
              {ICON_GROUPS.map((entry) => (
                <button
                  key={entry.label}
                  type="button"
                  onClick={() => setGroup(entry.label)}
                  className={`navpill ${entry.label === group ? 'navpill-on' : 'navpill-off'}`}
                >
                  {entry.label}
                </button>
              ))}
            </div>
          )}

          <div className="mt-2.5 grid max-h-52 grid-cols-8 gap-1 overflow-y-auto sm:grid-cols-12">
            {shown.map(([icon, words]) => (
              <button
                key={icon}
                type="button"
                title={words}
                aria-label={words}
                aria-pressed={icon === value}
                onClick={() => {
                  onChange(icon);
                  setOpen(false);
                  setQuery('');
                }}
                className={`grid h-9 place-items-center rounded-[3px] text-xl transition-colors ${
                  icon === value ? 'bg-leaf-600/20 ring-1 ring-leaf-600' : 'hover:bg-paper-200'
                }`}
              >
                {icon}
              </button>
            ))}
            {shown.length === 0 && (
              <p className="col-span-full py-4 text-center text-[13px] text-ink-500">
                Nothing matches “{query.trim()}”. You can paste any emoji below instead.
              </p>
            )}
          </div>

          {/* The grid is a shortcut, not a limit — anything the OS can render is
              still allowed, which is what keeps an unusual product unblocked. */}
          <label className="mt-2.5 flex items-center gap-2 border-t hairline pt-2.5">
            <span className="sign text-[10px] text-ink-500">Or paste your own</span>
            <input
              value={value}
              maxLength={8}
              onChange={(event) => onChange(event.target.value)}
              className="w-20 rounded-[3px] border border-paper-300 bg-paper-50 px-2 py-1 text-center text-[16px] focus:border-leaf-600"
            />
          </label>
        </div>
      )}
    </div>
  );
}
